/**
 * Lifecycle of a stored file (TO-324). A file is `PENDING` from the moment an
 * upload URL is issued and becomes `AVAILABLE` only once the service has
 * observed the object in S3 — never on the caller's say-so. Persisted as
 * `varchar` (never a Postgres ENUM) per CLAUDE.md "Enums Over Literals".
 */
export enum FileStatus {
	/** An upload URL (or multipart session) exists; the bytes are not confirmed. */
	PENDING = 'PENDING',
	/** The service has checked a pinned object version and the file may be served. */
	AVAILABLE = 'AVAILABLE',
	/** The checked version awaits a malware verdict (scanned domains, scanning on — TO-443). Never served. */
	SCANNING = 'SCANNING',
	/** Terminal: the bytes failed a content check or a malware scan (TO-443). Never served. */
	QUARANTINED = 'QUARANTINED',
}

/**
 * What a stored file IS (TO-443). Drives its bucket, key slug, `sensitivity`
 * tag and whether uploads are scanned — see file-service's domain policy.
 * Persisted as `varchar` with a CHECK, never a Postgres ENUM.
 */
export enum FileDomain {
	/** A user's own upload through the HTTP presign path. */
	USER_UPLOAD = 'USER_UPLOAD',
	/** A product image uploaded through the HTTP presign path. */
	PRODUCT_IMAGE = 'PRODUCT_IMAGE',
	/** A rendered offer PDF, written only through `FileStoragePort.store`. */
	OFFER_PDF = 'OFFER_PDF',
	/** A rendered offer landing page, written only through `FileStoragePort.store`. */
	OFFER_LANDING = 'OFFER_LANDING',
	/** A tenant's public branding image, written only by the branding route. */
	TENANT_BRANDING = 'TENANT_BRANDING',
}

/** The bucket a file lives in — by provenance (TO-443 design §2). */
export enum FileBucket {
	/** User-uploaded, untrusted, private. */
	TENANT_FILES = 'TENANT_FILES',
	/** System-generated, provenance-bound, private. */
	TENANT_GEN_FILES = 'TENANT_GEN_FILES',
	/** Rasterized public branding, served through the CDN. */
	PUBLIC_ASSETS = 'PUBLIC_ASSETS',
}

/** The `sensitivity` object tag (TO-443). Service-derived from the domain, never caller input. */
export enum FileSensitivity {
	PUBLIC = 'public',
	INTERNAL = 'internal',
	PII = 'pii',
	LEGAL = 'legal',
}

/** Kind of data subject a stored file concerns (DSAR linkage, TO-443). */
export enum FileSubjectType {
	CUSTOMER = 'CUSTOMER',
	USER = 'USER',
}

/** Why a file entered `QUARANTINED` (carried on `file.quarantined`). */
export enum FileQuarantineReason {
	SIZE_MISMATCH = 'SIZE_MISMATCH',
	TENANT_METADATA_MISMATCH = 'TENANT_METADATA_MISMATCH',
	CONTENT_MISMATCH = 'CONTENT_MISMATCH',
	THREAT_FOUND = 'THREAT_FOUND',
	SCAN_FAILED = 'SCAN_FAILED',
}

/**
 * Terminal (and intermediate) state of one processing concern within one run.
 * `PENDING` → `RUNNING` → one of `COMPLETED` / `FAILED` / `SKIPPED`.
 */
export enum FileProcessingStatus {
	PENDING = 'PENDING',
	RUNNING = 'RUNNING',
	COMPLETED = 'COMPLETED',
	FAILED = 'FAILED',
	/** The concern was considered but did not run — disabled, or a dependency did not complete. */
	SKIPPED = 'SKIPPED',
}

/** Lifecycle of a multipart upload session. */
export enum FileMultipartSessionStatus {
	OPEN = 'OPEN',
	COMPLETED = 'COMPLETED',
	ABORTED = 'ABORTED',
}

/**
 * The post-upload processing concerns the registry knows about. A concern is a
 * registry entry plus a class — adding one (OCR, transcription, real malware
 * scanning) needs no change to the upload path, the scheduler or the events.
 */
export enum FileProcessingConcern {
	/** Produces a WebP derivative of an image file. */
	THUMBNAIL = 'thumbnail',
	/** Registered but disabled placeholder — records `SKIPPED` until a scanner is wired. */
	VIRUS_SCAN = 'virus-scan',
}

/**
 * A stored file's metadata. The bytes live in S3 at {@link IFile.storageKey};
 * this row is the only thing the platform indexes, filters or returns.
 *
 * `entityType` / `entityId` are **opaque**: either both null or both non-null
 * 1–64-character strings. Nothing in file-service interprets them or checks
 * them against another table, so a new consumer attaches files by passing its
 * own strings with no change here.
 */
export interface IFile {
	fileId: string
	/** The tenant that owns the file — from the DB-resolved principal, never request input. */
	tenantId: string
	/** The user whose upload created the file; the only actor who may read, rename or delete it. */
	ownerUserId: string
	/** Caller-supplied display name (1–255 code points). Metadata only — never part of a storage key. */
	fileName: string
	contentType: string
	sizeBytes: number
	/**
	 * Service-derived S3 key (`{tenantId}/{domain-slug}/…/{fileId}`; legacy rows
	 * `files/{tenantId}/{fileId}`). Internal — it carries the physical tenant
	 * prefix and is never exposed on a DTO.
	 */
	storageKey: string
	status: FileStatus
	/** What the file is (TO-443). */
	domain: FileDomain
	/** Opaque owning-entity kind, or null. Null iff {@link IFile.entityId} is null. */
	entityType?: string | null
	/** Opaque owning-entity id, or null. Null iff {@link IFile.entityType} is null. */
	entityId?: string | null
	/**
	 * Derived soft-delete flag — `deletedAt` is the single source of truth, so
	 * this is computed (`deletedAt != null`) and never stored as a column.
	 */
	isDeleted: boolean
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

/**
 * An in-flight multipart upload. Which parts S3 already holds is read live from
 * S3 (`ListParts`), never mirrored here, so there is no second copy to drift.
 */
export interface IFileMultipartSession {
	sessionId: string
	fileId: string
	tenantId: string
	ownerUserId: string
	/** The S3 multipart upload id. Internal — never exposed on a DTO. */
	uploadId: string
	storageKey: string
	partCount: number
	status: FileMultipartSessionStatus
	/** 86400 seconds after the session was opened. */
	expiresAt: Date
	isDeleted: boolean
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

/**
 * The outcome of one concern within one processing run. `runId` is the
 * triggering event's `eventId`, so a redelivered SQS message resolves to the
 * same run (and no-ops) while a reprocess request — a new event — is a new run
 * with its own rows.
 */
export interface IFileProcessingResult {
	resultId: string
	fileId: string
	tenantId: string
	/** The triggering event's `eventId`. Unique per `(fileId, runId, concern)`. */
	runId: string
	concern: FileProcessingConcern
	status: FileProcessingStatus
	/** Why a `FAILED` concern failed, or why a `SKIPPED` one did not run. */
	detail?: string | null
	isDeleted: boolean
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

/** Response of `POST /files/upload-url` — the presigned PUT and where it lands. */
export interface IFileUploadUrl {
	fileId: string
	uploadUrl: string
	/** 900 seconds after issuance. */
	expiresAt: Date
}

/** Response of `GET /files/{fileId}/download-url`. */
export interface IFileDownloadUrl {
	downloadUrl: string
	/** 900 seconds after issuance. */
	expiresAt: Date
}

/** One part of a multipart upload and the presigned PUT that uploads it. */
export interface IFileMultipartPart {
	partNumber: number
	uploadUrl: string
}

/** Response of `POST /files/multipart` — the session and one URL per part. */
export interface IFileMultipartSessionCreated {
	sessionId: string
	fileId: string
	parts: IFileMultipartPart[]
	/** 86400 seconds after the session was opened. */
	expiresAt: Date
}

/**
 * Response of `GET /files/multipart/{sessionId}` — what S3 already holds and a
 * fresh presigned URL for each part it does not, so a caller re-uploads only
 * what is missing.
 */
export interface IFileMultipartSessionState {
	sessionId: string
	status: FileMultipartSessionStatus
	uploadedParts: number[]
	outstandingParts: IFileMultipartPart[]
	expiresAt: Date
}

/** Response of `POST /files/{fileId}/reprocess`. */
export interface IFileReprocessAccepted {
	fileId: string
	requestedAt: Date
}

/**
 * The media types an upload may declare. Deliberately an allowlist: an upload
 * naming anything else is refused with `FILE_CONTENT_TYPE_INVALID` rather than
 * stored and reasoned about later.
 */
export const ACCEPTED_FILE_CONTENT_TYPES = [
	'application/pdf',
	'image/png',
	'image/jpeg',
	'image/webp',
	'image/tiff',
	'text/csv',
	'text/plain',
	'application/json',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const

/** The domains the HTTP upload and multipart routes accept; absent means `USER_UPLOAD` (TO-443 BR-2). */
export const FILE_HTTP_UPLOAD_DOMAINS = [FileDomain.USER_UPLOAD, FileDomain.PRODUCT_IMAGE] as const

/** Presigned upload/download URL lifetime, in seconds (spec › Durations). */
export const FILE_PRESIGNED_URL_TTL_SECONDS = 900

/** Multipart session lifetime, in seconds (spec › Durations). */
export const FILE_MULTIPART_SESSION_TTL_SECONDS = 86400

/** Largest single file the service accepts, in bytes (5 GiB). */
export const FILE_MAX_SIZE_BYTES = 5368709120

/** Largest number of parts a multipart session may declare (the S3 ceiling). */
export const FILE_MAX_PART_COUNT = 10000
